import React from 'react';
import { Target, Users, Award, Eye, Heart, Zap, TrendingUp, Shield } from 'lucide-react';
import teamImage from '../../assets/business-meeting.jpg';
import africanBusinessman from '../../assets/african-man-black-suit_1157-45575.jpg_rbjcix.jpg';

const AboutPage = () => {
  const differentiators = [
    {
      icon: TrendingUp,
      title: 'Diversified Expertise',
      description: 'Our team strategically manages investments across Blockchain-based Assets, Real Estate, Oil and Gas, and Stocks, delivering a well-balanced portfolio and effective risk mitigation to support long-term growth.',
      color: 'from-gold-500 to-gold-600'
    },
    {
      icon: Zap,
      title: 'Tech Integration',
      description: 'We use cutting-edge technology and advanced analytics to offer real-time insights, empowering clients to optimize their portfolios for maximum returns.',
      color: 'from-navy-500 to-navy-600'
    },
    {
      icon: Eye,
      title: 'Global Insights',
      description: 'With expertise in key international markets, we identify and capitalize on global investment opportunities, providing access to high-growth markets.',
      color: 'from-gold-500 to-gold-600'
    },
    {
      icon: Shield,
      title: 'Sustainable Practices',
      description: 'We integrate ESG factors into our decisions, aligning with our values and identifying resilient investments for long-term value.',
      color: 'from-navy-500 to-navy-600'
    },
    {
      icon: Target,
      title: 'Risk Management',
      description: 'Our robust framework vets all decisions and adjusts strategies to safeguard investments against market volatility and challenges.',
      color: 'from-gold-500 to-gold-600'
    },
    {
      icon: Heart,
      title: 'Client Focus',
      description: 'We offer personalized investment solutions tailored to individual goals, with dedicated advisors providing continuous support and guidance.',
      color: 'from-navy-500 to-navy-600'
    }
  ];

  const coreValues = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our financial services, ensuring superior results for our clients.'
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Trust is the foundation of our relationships. We operate with complete transparency and ethical standards.'
    },
    {
      icon: Users,
      title: 'Client-Centric',
      description: 'Your success is our success. We tailor our strategies to meet your unique financial objectives and needs.'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'We embrace cutting-edge technology and innovative approaches to stay ahead in the financial markets.'
    }
  ];

  const stats = [
            { number: '$4.2B+', label: 'Worth of Assets Managed' },
    { number: '40K+', label: 'Clients' },
            { number: '10+', label: 'Years of Service' },
    { number: '100+', label: 'Active Agents' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/80 to-navy-800/60"></div>
        <div className="absolute inset-0">
          <img
            src={teamImage}
            alt="About Us"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold font-serif mb-4">About Us</h1>
          <p className="text-xl md:text-2xl text-gray-200">
            Home / <span className="text-gold-400">About</span>
          </p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-6">
              About Us
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                COREVEST is a leading financial services firm dedicated to helping individuals and businesses achieve their financial goals. We deliver comprehensive investment solutions and financial planning services with a commitment to transparency, security, and excellence.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our seasoned team combines deep expertise in investment management, financial planning, and strategic advisory services to offer a comprehensive suite of products and services. Whether you're seeking growth, security, or diversification, COREVEST is here to help you reach your financial goals with confidence.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We are committed to delivering unparalleled investment management and financial services tailored to meet the unique needs of our clients. Our focus on innovative financial solutions and data-driven market insights ensures that we approach each investment opportunity with meticulous attention to detail and a dedication to optimizing returns while minimizing risk.
              </p>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src={teamImage}
                  alt="COREVEST Team"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"></div>
              </div>
              
              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                <div className="flex items-center space-x-4">
                  <img
                    src={africanBusinessman}
                    alt="CEO"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h5 className="font-semibold text-navy-900">Expert Leadership</h5>
                    <p className="text-sm text-gray-600">10+ Years Experience</p>
                    <div className="flex items-center mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Award key={i} className="h-3 w-3 text-gold-500 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="mb-20">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                Our Mission & Vision
              </h3>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="flex items-start space-x-6 group">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-navy-900 mb-4">Our Mission</h4>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    To empower individuals and businesses to achieve their financial goals through comprehensive investment management, strategic financial planning, and innovative solutions. We are committed to delivering exceptional value, transparency, and personalized service that builds lasting wealth and financial security.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-6 group">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-navy-500 to-navy-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-navy-900 mb-4">Our Vision</h4>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    To be the leading financial services provider, recognized for our integrity, 
                    innovation, and exceptional client outcomes in wealth management.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-20">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                Our <span className="text-gradient bg-gradient-to-r from-gold-500 to-gold-600 bg-clip-text text-transparent">Core</span> Values
              </h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our core values guide every decision we make and every relationship we build, ensuring we deliver exceptional service with integrity and excellence.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreValues.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <div key={index} className="text-center group">
                    <div className="inline-flex p-4 bg-gradient-to-r from-gold-500 to-gold-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-semibold text-navy-900 mb-4">
                      {value.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* What Makes Us Different */}
          <div className="mb-20">
            <div className="text-center mb-16">
              <h3 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4">
                What Makes Us Different?
              </h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                At COREVEST, we stand out in the investment landscape by offering a comprehensive, innovative approach that caters to a diverse range of financial goals. Our commitment to excellence and strategic investment ensures that our clients receive unparalleled value and growth opportunities.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {differentiators.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className={`inline-flex p-4 bg-gradient-to-r ${item.color} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-4">
                      {item.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats Section */}
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-12 text-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {stats.map((stat, index) => (
                <div key={index} className="group">
                  <div className="text-3xl lg:text-4xl font-bold text-gold-400 mb-2 group-hover:scale-110 transition-transform duration-300">
                    {stat.number}
                  </div>
                  <div className="text-gray-300 text-lg">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage; 